/**
 * Shared display formatters for admin panels. Server- and client-safe (no React).
 * Digit conventions differ per surface (e.g. 6-decimal micro-costs on the raw requests
 * table vs 2–6 on classification records), so currency takes explicit digit options.
 */

const numberFormatters = new Map<string, Intl.NumberFormat>();

function getNumberFormat(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options);
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", options);
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

/**
 * Postgres returns numeric/bigint aggregates as strings; coerce to a finite
 * number or 0. Shared by the formatters below and by action row-mappers.
 */
export function toFiniteNumber(value: string | number | null | undefined): number {
  const number = typeof value === "number" ? value : Number.parseFloat(value ?? "0");
  return Number.isFinite(number) ? number : 0;
}

export interface CurrencyFormatOptions {
  minFractionDigits?: number;
  maxFractionDigits?: number;
}

export function formatCurrencyUSD(
  value: string | number | null | undefined,
  { minFractionDigits = 2, maxFractionDigits = 6 }: CurrencyFormatOptions = {},
): string {
  return getNumberFormat({
    style: "currency",
    currency: "USD",
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  }).format(toFiniteNumber(value));
}

export function formatInteger(value: string | number | null | undefined): string {
  return getNumberFormat({ maximumFractionDigits: 0 }).format(toFiniteNumber(value));
}

/**
 * A 0–1 fraction as a percentage: "—" for null (genuinely absent), "<0.1%"
 * for a positive-but-tiny share so a real contribution never rounds to 0%.
 */
export function formatShare(share: number | null): string {
  if (share === null) return "—";
  if (share > 0 && share < 0.001) return "<0.1%";
  return `${(share * 100).toFixed(1)}%`;
}

/**
 * formatInteger with an honest "—" for null instead of a fabricated 0 —
 * for stats that are genuinely absent, e.g. token percentiles of a group
 * with only character-billed (audio) rows (AIR-1117).
 */
export function formatIntegerOrDash(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : formatInteger(value);
}

export function formatDurationMs(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  if (value < 1000) return `${value} ms`;
  return `${(value / 1000).toFixed(2)} s`;
}
